import React, { useState } from "react";
import { Loader2, KeyRound } from "lucide-react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * Modal for a logged-in admin to change their OWN password.
 *
 * Backed by POST /admin/change-password which requires the current password.
 * On success the server also emails a "your password just changed" receipt
 * so an attacker who plants a change gets flagged immediately.
 *
 * When ``forced=true`` (peer-reset just happened, server set
 * ``password_change_required=true``), the modal:
 *   - hides the Cancel button
 *   - refuses to close on backdrop click / Escape
 *   - shows an amber "you must rotate" banner
 *   - auto-fills nothing — the admin has to type the temp password AND
 *     pick a fresh one only they know.
 */
export default function AdminChangePasswordDialog({ open, onOpenChange, forced = false }) {
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [saving, setSaving] = useState(false);

    const tooShort = next.length > 0 && next.length < 8;
    const mismatch = confirm.length > 0 && confirm !== next;
    const reused = next.length > 0 && next === current;
    const disabled =
        saving || !current || next.length < 8 || confirm !== next || next === current;

    const reset = () => { setCurrent(""); setNext(""); setConfirm(""); };

    const submit = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            await api.post("/admin/change-password", {
                current_password: current,
                new_password: next,
            });
            toast.success("Password updated. Use it next time you sign in.");
            reset();
            onOpenChange(false);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog
            open={open}
            onOpenChange={(v) => {
                // Forced mode: block user-initiated close attempts. Only close
                // after a successful password submission (handled below).
                if (forced && open && !v) return;
                if (!v) reset();
                onOpenChange(v);
            }}
        >
            <DialogContent
                data-testid="admin-change-pw-dialog"
                onEscapeKeyDown={(e) => { if (forced) e.preventDefault(); }}
                onPointerDownOutside={(e) => { if (forced) e.preventDefault(); }}
                onInteractOutside={(e) => { if (forced) e.preventDefault(); }}
                className={forced ? "[&>button.absolute]:hidden" : undefined}
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <KeyRound size={16} className="text-emerald-500" />
                        {forced ? "Rotate your temporary password" : "Change my password"}
                    </DialogTitle>
                    <DialogDescription>
                        {forced
                            ? "A peer platform admin just reset your password. Pick a new one that only you know before continuing."
                            : "You'll stay signed in. A security receipt is emailed to you when your password changes."}
                    </DialogDescription>
                </DialogHeader>
                {forced && (
                    <div
                        data-testid="admin-change-pw-forced-banner"
                        className="rounded-lg border border-amber-400/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-300"
                    >
                        You can&apos;t use the admin console until you rotate the temporary password. This modal will close once you do.
                    </div>
                )}
                <form onSubmit={submit} className="space-y-3 py-2">
                    <div>
                        <Label htmlFor="cp-current">Current password</Label>
                        <Input
                            id="cp-current"
                            type="password"
                            autoFocus
                            required
                            value={current}
                            onChange={(e) => setCurrent(e.target.value)}
                            data-testid="admin-change-pw-current"
                            className="mt-1"
                        />
                    </div>
                    <div>
                        <Label htmlFor="cp-next">New password</Label>
                        <Input
                            id="cp-next"
                            type="password"
                            required
                            minLength={8}
                            value={next}
                            onChange={(e) => setNext(e.target.value)}
                            data-testid="admin-change-pw-next"
                            className="mt-1"
                        />
                        {tooShort && <p className="text-xs text-rose-500 mt-1">At least 8 characters.</p>}
                        {reused && <p className="text-xs text-rose-500 mt-1">Pick something different from your current password.</p>}
                    </div>
                    <div>
                        <Label htmlFor="cp-confirm">Confirm new password</Label>
                        <Input
                            id="cp-confirm"
                            type="password"
                            required
                            value={confirm}
                            onChange={(e) => setConfirm(e.target.value)}
                            data-testid="admin-change-pw-confirm"
                            className="mt-1"
                        />
                        {mismatch && <p className="text-xs text-rose-500 mt-1">Passwords don&apos;t match.</p>}
                    </div>
                    <DialogFooter className="pt-2">
                        {!forced && (
                            <button
                                type="button"
                                onClick={() => onOpenChange(false)}
                                className="btn-outline text-sm px-4 py-2"
                                data-testid="admin-change-pw-cancel"
                            >
                                Cancel
                            </button>
                        )}
                        <button
                            type="submit"
                            disabled={disabled}
                            className="btn-green text-sm px-4 py-2 disabled:opacity-60"
                            data-testid="admin-change-pw-submit"
                        >
                            {saving ? <Loader2 size={13} className="animate-spin" /> : forced ? "Rotate password" : "Update password"}
                        </button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
